import type {
  EventsApproval,
  EventsBooking,
  EventsPipeline,
} from '../../features/events/api/types';
import { approvalActionLabel } from '../../features/events/approvals';
import { formatMoney } from '../../features/events/proposals';
import { intentHue } from '../../shared/intent';
import { formatPhone } from '../../shared/phone';
import { relativeTime } from '../../shared/time';
import type { BreakdownRow, Hue, Tone } from '../../ui/v2';
import { cateringRecordPath } from '../SpecialCatering/cateringRoutes';
import {
  FULFILLMENT_LABEL,
  formatCurrencyFromCents,
  listFulfillmentType,
} from '../Tables/orderModel';
import { sentenceCaseStatus } from '../Tables/reservationModel';

// ---------------------------------------------------------------------------
// Action items — one grouped list of work waiting for a person
// ---------------------------------------------------------------------------

export type AttentionKind =
  | 'sms'
  | 'voicemail'
  | 'payment'
  | 'approval'
  | 'event_inquiry'
  | 'catering_inquiry'
  | 'knowledge_question';

export interface AttentionItem {
  /** Unique across groups (`kind:id`). */
  id: string;
  /** Guest / caller name; null when Arthur didn't capture one. */
  who: string | null;
  /** First line when `who` is null ("Unknown caller", "Booking #3107"). */
  fallback: string;
  /** Quiet second line: phone, event · date · guests, booking and amount. */
  detail: string | null;
  /** One sentence: what Arthur heard or what happened. */
  summary: string;
  /** ISO timestamp the row is aged by. */
  at: string | null;
  status: { label: string; tone: Tone; hollow?: boolean };
  href: string;
}

export interface AttentionGroup {
  kind: AttentionKind;
  /** Total in the source list (the API `count`), not just the rows shown. */
  count: number;
  /** Newest first; the page shows at most `ROWS_PER_GROUP`. */
  items: AttentionItem[];
}

export interface NeedsYou {
  groups: AttentionGroup[];
  /** Groups the store has whose request failed; the rest still render. */
  failed: AttentionKind[];
}

export const ROWS_PER_GROUP = 5;

/**
 * `label` names the group and its page; `one` names a single row ("Voicemail
 * · (512) 555-0142"); `hue` is the group's categorical colour, shared with
 * the intent map where the two coincide (voicemail sky, events violet).
 */
export const GROUP_META: Record<
  AttentionKind,
  { label: string; one: string; href: string; hue: Hue }
> = {
  sms: {
    label: 'Text messages',
    one: 'Text message',
    href: '/sms',
    hue: 'teal',
  },
  voicemail: {
    label: 'Voicemail',
    one: 'Voicemail',
    href: '/voicemail?unread=true',
    hue: 'sky',
  },
  payment: {
    label: 'Payments',
    one: 'Payment',
    href: '/events/pipeline',
    hue: 'amber',
  },
  approval: {
    label: 'Approvals',
    one: 'Approval',
    href: '/events/approvals',
    hue: 'amber',
  },
  event_inquiry: {
    label: 'Event inquiries',
    one: 'Event inquiry',
    href: '/special-events',
    hue: 'violet',
  },
  catering_inquiry: {
    label: 'Catering inquiries',
    one: 'Catering inquiry',
    href: '/catering/inquiries',
    hue: 'violet',
  },
  knowledge_question: {
    label: 'Questions to answer',
    one: 'FAQ',
    href: '/knowledge-questions',
    hue: 'lime',
  },
};

/** Reading order: a person is waiting → money → new work. */
export const GROUP_ORDER: AttentionKind[] = [
  'sms',
  'voicemail',
  'payment',
  'approval',
  'event_inquiry',
  'catering_inquiry',
  'knowledge_question',
];

/** Groups in reading order, without the empty ones. */
export function orderedGroups(groups: AttentionGroup[]): AttentionGroup[] {
  return GROUP_ORDER.flatMap((kind) =>
    groups.filter((g) => g.kind === kind && g.count > 0),
  );
}

export function totalAttention(groups: AttentionGroup[]): number {
  return groups.reduce((n, g) => n + Math.max(0, g.count), 0);
}

/** Rows the home screen shows: each group's fair share first, then at most this many. */
export const QUEUE_SHARE = 2;
export const QUEUE_MAX = 6;

export interface QueueItem extends AttentionItem {
  kind: AttentionKind;
}

/**
 * The home screen's queue: one flat list in reading order (group priority,
 * then newest first within a group). Every group is dealt up to `share`
 * rows, one at a time, so a busy inbox never hides a quieter one even when
 * `share` times the group count would pass `max`. Whatever room is left
 * goes to the groups in priority order. The per-group links under the list
 * carry the full counts.
 */
export function queueItems(
  groups: AttentionGroup[],
  share = QUEUE_SHARE,
  max = QUEUE_MAX,
): QueueItem[] {
  const ordered = orderedGroups(groups);
  const take = ordered.map(() => 0);
  let room = max;
  for (let round = 0; round < share && room > 0; round++) {
    for (let i = 0; i < ordered.length && room > 0; i++) {
      if (ordered[i].items.length > take[i]) {
        take[i] += 1;
        room -= 1;
      }
    }
  }
  for (let i = 0; i < ordered.length && room > 0; i++) {
    const extra = Math.min(room, ordered[i].items.length - take[i]);
    take[i] += extra;
    room -= extra;
  }
  return ordered.flatMap((g, i) =>
    g.items.slice(0, take[i]).map((item) => ({ ...item, kind: g.kind })),
  );
}

/** `GET /api/voicemails/` row (`VoicemailListSerializer`). */
export interface ApiVoicemail {
  id: number;
  caller_phone?: string | null;
  ai_summary?: string | null;
  vip_name?: string | null;
  call_time?: string | null;
  created_at?: string | null;
}

/** `GET /api/messaging/sms/conversations/` row. */
export interface ApiSmsConversation {
  id: number;
  customer_name?: string | null;
  customer_phone_e164?: string | null;
  last_message_preview?: string | null;
  last_message_at?: string | null;
  unread_count?: number | null;
}

/** `GET /api/event-inquiries/` and `/api/catering-inquiries/` row. */
export interface ApiInquiry {
  id: number;
  guest_name?: string | null;
  phone?: string | null;
  caller_phone?: string | null;
  occasion?: string | null;
  preferred_date?: string | null;
  guest_count?: string | number | null;
  special_requests?: string | null;
  ai_generated_summary?: string | null;
  created_at?: string | null;
}

/** `GET /api/knowledge-questions/` row. */
export interface ApiKnowledgeQuestion {
  id: number;
  question: string;
  occurrence_count: number;
  last_seen_at?: string | null;
  latest_caller_question?: string | null;
}

const clean = (s: string | null | undefined) => {
  const t = s?.trim();
  return t ? t : null;
};

/** Name when Arthur captured one; otherwise the number, quietly; otherwise "Unknown caller". */
function person(
  name: string | null | undefined,
  phone: string | null | undefined,
) {
  const who = clean(name);
  const number = clean(formatPhone(phone));
  return {
    who,
    fallback: number ?? 'Unknown caller',
    phone: who ? number : null,
  };
}

const joinDetail = (...parts: (string | null | undefined | false)[]) =>
  parts.filter(Boolean).join(' · ') || null;

export function voicemailItem(v: ApiVoicemail): AttentionItem {
  const p = person(v.vip_name, v.caller_phone);
  return {
    id: `voicemail:${v.id}`,
    who: p.who,
    fallback: p.fallback,
    detail: p.phone,
    summary: clean(v.ai_summary) ?? 'No summary yet.',
    at: v.call_time ?? v.created_at ?? null,
    status: { label: 'Unread', tone: 'accent' },
    href: GROUP_META.voicemail.href,
  };
}

export function smsItem(c: ApiSmsConversation): AttentionItem {
  const p = person(c.customer_name, c.customer_phone_e164);
  const unread = c.unread_count ?? 0;
  return {
    id: `sms:${c.id}`,
    who: p.who,
    fallback: p.fallback,
    detail: joinDetail(p.phone, unread > 0 && `${unread} unread`),
    summary: clean(c.last_message_preview) ?? '',
    at: c.last_message_at ?? null,
    status: { label: 'Waiting for you', tone: 'warning', hollow: true },
    href: GROUP_META.sms.href,
  };
}

function guestsLabel(count: string | number | null | undefined): string | null {
  const raw = clean(count == null ? null : String(count));
  if (!raw) return null;
  return /^\d+$/.test(raw) ? `${raw} ${raw === '1' ? 'guest' : 'guests'}` : raw;
}

/**
 * `preferred_date` is free text ("June 10", "next Friday"); only an ISO date
 * is reformatted, as a local calendar date so it doesn't shift a day.
 */
export function preferredDateLabel(
  raw: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const text = clean(raw);
  const iso = text?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!iso) return text;
  const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
  });
}

export function inquiryItem(
  kind: 'event_inquiry' | 'catering_inquiry',
  i: ApiInquiry,
): AttentionItem {
  const p = person(i.guest_name, i.phone || i.caller_phone);
  return {
    id: `${kind}:${i.id}`,
    who: p.who,
    fallback: p.fallback,
    detail: joinDetail(
      clean(i.occasion),
      preferredDateLabel(i.preferred_date),
      guestsLabel(i.guest_count),
    ),
    summary: clean(i.ai_generated_summary) ?? clean(i.special_requests) ?? '',
    at: i.created_at ?? null,
    status: { label: 'New', tone: 'accent' },
    href:
      kind === 'catering_inquiry'
        ? cateringRecordPath(i.id)
        : GROUP_META.event_inquiry.href,
  };
}

export function knowledgeQuestionItem(q: ApiKnowledgeQuestion): AttentionItem {
  const asked = q.occurrence_count;
  return {
    id: `knowledge_question:${q.id}`,
    who: null,
    fallback: clean(q.question) ?? 'Caller question',
    detail: asked === 1 ? 'Asked once' : `Asked ${asked} times`,
    summary: clean(q.latest_caller_question)
      ? ''
      : 'Arthur didn’t have the answer.',
    at: q.last_seen_at ?? null,
    status: { label: 'Needs an answer', tone: 'warning', hollow: true },
    href: `${GROUP_META.knowledge_question.href}/${q.id}`,
  };
}

const APPROVAL_SUMMARY: Record<string, string> = {
  'proposal.publish':
    'A proposal is waiting for approval before it goes to the guest.',
  'payment.refund': 'A refund is waiting for approval.',
  'confirmation.resolve': 'A confirmation is blocked and needs a decision.',
};

export function approvalItem(a: EventsApproval): AttentionItem {
  const title = approvalActionLabel(a.action);
  return {
    id: `approval:${a.id}`,
    who: title,
    fallback: title,
    detail: joinDetail(
      a.booking_id != null && `Booking #${a.booking_id}`,
      a.amount_cents != null && formatMoney(a.amount_cents),
    ),
    summary: APPROVAL_SUMMARY[a.action] ?? 'Waiting for an approver.',
    at: a.created_at,
    status: { label: 'Pending', tone: 'warning', hollow: true },
    href: GROUP_META.approval.href,
  };
}

/**
 * Bookings whose money needs a person. The pipeline row carries the
 * booking's `payment_state`, so a past-due deposit is visible here; a failed
 * payment attempt or refund lives on the booking's payment schedule and
 * refunds, which the pipeline doesn't include.
 */
export function paymentBookings(pipeline: EventsPipeline): EventsBooking[] {
  const seen = new Set<number>();
  return Object.values(pipeline)
    .flat()
    .filter((b) => {
      if (b.payment_state !== 'PAST_DUE' || seen.has(b.id)) return false;
      seen.add(b.id);
      return true;
    })
    .sort((a, z) => z.updated_at.localeCompare(a.updated_at));
}

export function paymentItem(b: EventsBooking): AttentionItem {
  const booking = `Booking #${b.id}`;
  const who = clean(b.primary_contact?.name);
  return {
    id: `payment:${b.id}`,
    who,
    fallback: booking,
    detail: who ? joinDetail(booking, clean(b.event?.event_type)) : null,
    summary: 'The deposit is past due.',
    at: b.updated_at,
    status: { label: 'Past due', tone: 'danger' },
    href: `/events/bookings/${b.id}`,
  };
}

// ---------------------------------------------------------------------------
// This month — figures
// ---------------------------------------------------------------------------

/** The fields production reads from the calls / orders / reservations payloads. */
export interface DashboardStats {
  callsMonth: number | null;
  callsAll: number | null;
  callMinutesMonth: number | null;
  callMinutesAll: number | null;
  ordersMonth: number | null;
  ordersAll: number | null;
  orderValueMonth: number | null;
  orderValueAll: number | null;
  reservationsMonth: number | null;
  reservationsAll: number | null;
  /** Null when no per-guest cover could be resolved, so the box stays hidden. */
  reservationRevenueMonth: number | null;
  reservationRevenueAll: number | null;
  /** Dollars per guest used for the estimate, or null when unknown. */
  reservationCover: number | null;
  /**
   * Bookings Arthur placed this month, including ones later cancelled.
   * Null when the payload does not say, so the headline never treats
   * every OpenTable reservation as Arthur's.
   */
  arthurReservationsMonth: number | null;
  /** Successfully placed voice orders this month. Null when unverified. */
  arthurOrdersMonth: number | null;
  /** Answered calls in the rolling past seven days. Null when unverified. */
  callsLast7Days: number | null;
  eventInquiriesMonth: number | null;
  eventInquiriesAll: number | null;
  /** SMS conversations started, not individual messages. */
  smsMonth: number | null;
  smsAll: number | null;
  /** Inbox and archived together. */
  voicemailsMonth: number | null;
  voicemailsAll: number | null;
}

export const EMPTY_STATS: DashboardStats = {
  callsMonth: null,
  callsAll: null,
  callMinutesMonth: null,
  callMinutesAll: null,
  ordersMonth: null,
  ordersAll: null,
  orderValueMonth: null,
  orderValueAll: null,
  reservationsMonth: null,
  reservationsAll: null,
  reservationRevenueMonth: null,
  reservationRevenueAll: null,
  reservationCover: null,
  arthurReservationsMonth: null,
  arthurOrdersMonth: null,
  callsLast7Days: null,
  eventInquiriesMonth: null,
  eventInquiriesAll: null,
  smsMonth: null,
  smsAll: null,
  voicemailsMonth: null,
  voicemailsAll: null,
};

/**
 * Paginated `count`s from the voicemail, SMS and event inquiry lists, read
 * once filtered to this month and once unfiltered. A read that failed or was
 * skipped (the channel is off) is null.
 */
export interface ChannelCounts {
  eventInquiriesMonth: Payload;
  eventInquiriesAll: Payload;
  smsMonth: Payload;
  smsAll: Payload;
  voicemailsMonth: Payload;
  voicemailsAll: Payload;
}

type Payload = Record<string, unknown> | null | undefined;

const num = (...values: unknown[]): number | null => {
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (
      typeof v === 'string' &&
      v.trim() !== '' &&
      Number.isFinite(Number(v))
    ) {
      return Number(v);
    }
  }
  return null;
};

/** A number the payload actually sent. Missing keys stay null, not zero. */
const optionalNum = (payload: Payload, key: string): number | null => {
  if (!payload || !(key in payload)) return null;
  return num(payload[key]);
};

/**
 * Same fields and fallbacks production read. A payload that failed to load
 * is `null`, and its figures stay `null` so they render as a dash.
 */
export function readStats(
  calls: Payload,
  orders: Payload,
  reservations: Payload | unknown[],
  channels: Partial<ChannelCounts> = {},
): DashboardStats {
  const stats = { ...EMPTY_STATS };
  for (const key of Object.keys(channels) as (keyof ChannelCounts)[]) {
    const page = channels[key];
    if (page) stats[key] = num(page.count);
  }
  if (calls) {
    stats.callsAll = num(calls.count) ?? 0;
    stats.callsMonth = num(
      calls.total_monthly_calls,
      calls.total_monthly_count,
      calls.monthly_count,
    );
    stats.callMinutesMonth = num(calls.total_monthly_minutes);
    stats.callMinutesAll = num(calls.total_minutes) ?? 0;
    stats.callsLast7Days = optionalNum(calls, 'total_calls_last_7_days');
  }
  if (orders) {
    stats.ordersAll = num(orders.count) ?? 0;
    stats.orderValueAll = num(orders.total_order_amount) ?? 0;
    stats.ordersMonth = num(orders.total_monthly_count, orders.monthly_count);
    stats.orderValueMonth = num(
      orders.total_monthly_order_amount,
      orders.monthly_total_order_amount,
    );
    stats.arthurOrdersMonth = optionalNum(orders, 'total_monthly_voice_orders');
  }
  if (Array.isArray(reservations)) {
    stats.reservationsAll = reservations.length;
  } else if (reservations) {
    stats.reservationsAll = num(reservations.count) ?? 0;
    stats.reservationsMonth = num(
      reservations.total_monthly_reservations,
      reservations.monthly_count,
      reservations.total_monthly_count,
    );
    stats.reservationRevenueMonth = num(reservations.reservation_revenue_month);
    stats.reservationRevenueAll = num(reservations.reservation_revenue_all);
    stats.reservationCover = num(reservations.reservation_cover);
    stats.arthurReservationsMonth = optionalNum(
      reservations,
      'total_monthly_arthur_reservations',
    );
  }
  return stats;
}

/**
 * Whether the store has a POS / OpenTable connected. `null` means the status
 * could not be read, and the figure is shown rather than hidden on a guess.
 */
export interface ProductGating {
  orders: boolean | null;
  reservations: boolean | null;
}

export const fmtNumber = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('en-US');

export const fmtMinutes = (m: number | null) =>
  m == null ? '—' : `${Math.round(m).toLocaleString('en-US')} min`;

/** Whole dollars: a summary, not a ledger. */
export const fmtMoney = (n: number | null) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(n);

/** Cover amounts keep cents when they are not a whole dollar. */
export const fmtCover = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);

/**
 * Which channels the store has switched on. Voicemail has no switch. SMS is
 * set up by AskArthur, so its card is left out rather than offered.
 */
export interface ChannelGating {
  events: boolean;
  sms: boolean;
}

export interface FigureDef {
  key: string;
  label: string;
  month: string;
  /** The all-time total, under the month's figure. */
  secondary: string;
  /** The figure is an estimate, footnoted in the strip header. */
  estimate?: boolean;
  /** One hue per product, shared with the activity rows. */
  hue: Hue;
  icon:
    | 'phone'
    | 'calendar'
    | 'users'
    | 'receipt'
    | 'dollar'
    | 'voicemail'
    | 'message';
  /** Set when the product is not connected: a link replaces the value. */
  connect?: { label: string; reason: string };
  /** Set when the store can switch the channel on from the card. */
  turnOn?: { label: string; reason: string };
}

const CONNECT: Record<keyof ProductGating, FigureDef['connect']> = {
  orders: {
    label: 'Connect a POS',
    reason: 'Orders appear here once a point of sale is connected.',
  },
  reservations: {
    label: 'Connect OpenTable',
    reason: 'Reservations appear here once OpenTable is connected.',
  },
};

const allTime = (n: number | null) => `${fmtNumber(n)} all time`;
const moneyAllTime = (n: number | null) => `${fmtMoney(n)} all time`;

/**
 * One card per channel, in a fixed order so a card never moves because
 * another channel had a busier month. Revenue sits beside the count it
 * belongs to. Cards waiting to be connected or switched on follow the live ones.
 */
export function buildFigures(
  stats: DashboardStats,
  gating: ProductGating,
  channels: ChannelGating,
): FigureDef[] {
  const figures: (FigureDef & {
    gate?: keyof ProductGating;
    /** Stays, as a connect link, when its gate is off. */
    lead?: boolean;
    show?: boolean;
  })[] = [
    {
      key: 'calls',
      label: 'Calls answered',
      month: fmtNumber(stats.callsMonth),
      secondary: allTime(stats.callsAll),
      hue: 'sky',
      icon: 'phone',
    },
    {
      key: 'reservations',
      label: 'Reservations',
      // The report names every booking Arthur placed, including ones later
      // cancelled. Reservations that came from OpenTable stay out.
      month: fmtNumber(
        stats.arthurReservationsMonth ?? stats.reservationsMonth,
      ),
      secondary: allTime(stats.reservationsAll),
      hue: 'violet',
      icon: 'calendar',
      gate: 'reservations',
      lead: true,
    },
    {
      key: 'reservationRevenue',
      label: 'Reservation revenue',
      month: fmtMoney(stats.reservationRevenueMonth),
      secondary: moneyAllTime(stats.reservationRevenueAll),
      estimate: true,
      hue: 'violet',
      icon: 'dollar',
      gate: 'reservations',
      show:
        stats.reservationRevenueMonth != null ||
        stats.reservationRevenueAll != null,
    },
    channels.events
      ? {
          key: 'events',
          label: 'Event inquiries',
          month: fmtNumber(stats.eventInquiriesMonth),
          secondary: allTime(stats.eventInquiriesAll),
          hue: 'amber',
          icon: 'users',
        }
      : {
          key: 'events',
          label: 'Event inquiries',
          month: '',
          secondary: 'Off',
          hue: 'amber',
          icon: 'users',
          turnOn: {
            label: 'Turn on',
            reason: 'Let Arthur take event requests on calls.',
          },
        },
    {
      key: 'orders',
      label: 'Orders',
      month: fmtNumber(stats.ordersMonth),
      secondary: allTime(stats.ordersAll),
      hue: 'teal',
      icon: 'receipt',
      gate: 'orders',
      lead: true,
    },
    {
      key: 'orderRevenue',
      label: 'Order revenue',
      month: fmtMoney(stats.orderValueMonth),
      secondary: moneyAllTime(stats.orderValueAll),
      hue: 'teal',
      icon: 'dollar',
      gate: 'orders',
    },
    {
      key: 'voicemails',
      label: 'Voicemails',
      month: fmtNumber(stats.voicemailsMonth),
      secondary: allTime(stats.voicemailsAll),
      hue: 'rose',
      icon: 'voicemail',
    },
    ...(channels.sms
      ? [
          {
            key: 'sms',
            label: 'SMS conversations',
            month: fmtNumber(stats.smsMonth),
            secondary: allTime(stats.smsAll),
            hue: 'accent' as const,
            icon: 'message' as const,
          },
        ]
      : []),
  ];
  const cards: FigureDef[] = figures
    .filter((f) => f.show !== false)
    .filter((f) => !f.gate || gating[f.gate] !== false || f.lead)
    .map(({ gate, lead: _lead, show: _show, ...f }) =>
      gate && gating[gate] === false
        ? {
            ...f,
            month: '',
            secondary: 'Not connected',
            estimate: undefined,
            connect: CONNECT[gate],
          }
        : f,
    );
  const waiting = (f: FigureDef) => Boolean(f.connect || f.turnOn);
  return [...cards.filter((f) => !waiting(f)), ...cards.filter(waiting)];
}

/** The span the strip covers, in the store's calendar: "October 1–5". */
export function monthToDate(todayLocal: string | null, now: Date): string {
  const { day, month } = localCalendar(todayLocal) ?? {
    day: now.getDate(),
    month: MONTHS[now.getMonth()],
  };
  return day === 1 ? `${month} 1` : `${month} 1–${day}`;
}

/**
 * The link that stands in for hidden figures ("Connect OpenTable"), or null.
 * `figuresReason` is its full sentence, used as the link's title.
 */
export function connectLabel(gating: ProductGating): string | null {
  const noOrders = gating.orders === false;
  const noReservations = gating.reservations === false;
  if (noOrders && noReservations) return 'Connect OpenTable or a POS';
  if (noOrders) return 'Connect a point of sale';
  if (noReservations) return 'Connect OpenTable';
  return null;
}

/** The one sentence explaining hidden figures, or null. */
export function figuresReason(gating: ProductGating): string | null {
  const noOrders = gating.orders === false;
  const noReservations = gating.reservations === false;
  if (noOrders && noReservations) {
    return 'Reservations and orders appear here once OpenTable or a point of sale is connected.';
  }
  if (noOrders) return 'Orders appear here once a point of sale is connected.';
  if (noReservations) {
    return 'Reservations appear here once OpenTable is connected.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Call insights — GET /api/dashboard/call-intelligence/
// ---------------------------------------------------------------------------

export interface CallIntelligenceSecondary {
  secondary_intent: string;
  label: string;
  count: number;
  percentage: number;
}

export interface CallIntelligencePrimary {
  primary_intent: string;
  label: string;
  count: number;
  percentage: number;
  secondary_breakdown: CallIntelligenceSecondary[];
}

export interface CallIntelligence {
  store_id: number;
  scope: string;
  period_start: string | null;
  period_end: string | null;
  total_calls: number;
  tagged_calls: number;
  untagged_calls: number;
  primary_breakdown: CallIntelligencePrimary[];
}

/** The backend's labels are Title Case ("Store Info"); the product is sentence case. */
export function sentenceCase(label: string): string {
  if (!label) return label;
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

/**
 * Integer percentages of `values` that sum to 100. Untagged calls are not
 * in the list, so they are not part of the share. Largest remainders get
 * the leftover points so independent rounding cannot land on 99 or 101.
 */
export function reasonShares(values: number[]): number[] {
  if (values.length === 0) return [];
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return values.map(() => 0);
  const parts = values.map((value, index) => {
    const exact = (value / total) * 100;
    const base = Math.floor(exact);
    return { index, value, base, frac: exact - base };
  });
  let left = 100 - parts.reduce((sum, part) => sum + part.base, 0);
  const order = [...parts].sort(
    (a, b) => b.frac - a.frac || b.value - a.value || a.index - b.index,
  );
  for (const part of order) {
    if (left <= 0) break;
    part.base += 1;
    left -= 1;
  }
  return parts.map((part) => part.base);
}

export function intelligenceRows(data: CallIntelligence): BreakdownRow[] {
  const kept = (data.primary_breakdown ?? [])
    .filter((b) => b.count > 0)
    .sort((a, z) => z.count - a.count);
  const shares = reasonShares(kept.map((b) => b.count));
  return kept.map((b, index) => {
    const top = [...(b.secondary_breakdown ?? [])]
      .filter((s) => s.count > 0)
      .sort((a, z) => z.count - a.count)[0];
    return {
      key: b.primary_intent,
      label: sentenceCase(b.label),
      value: b.count,
      percent: shares[index] ?? 0,
      tone: intentHue(b.primary_intent),
      sub: top ? `Most often: ${top.label.toLowerCase()}` : undefined,
    };
  });
}

/** Every call, tagged or not. The footer links this many rows to the calls table. */
export function callReasonLinkCount(data: CallIntelligence): number {
  return Math.max(0, data.tagged_calls) + Math.max(0, data.untagged_calls);
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

/** `busy_mode_status` from the store info payload, as a status label. */
export function arthurStatus(
  raw: string | null | undefined,
): { label: string; tone: Tone; hollow?: boolean } | null {
  switch (raw?.trim().toUpperCase()) {
    case 'ONLINE':
      return { label: 'Arthur is answering', tone: 'success' };
    case 'BUSY':
      return { label: 'Busy mode', tone: 'warning' };
    case 'PAUSED':
      return { label: 'Arthur paused', tone: 'neutral', hollow: true };
    default:
      return null;
  }
}

export function greeting(now = new Date()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function formatToday(now = new Date()): string {
  return now.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function monthLabel(now = new Date()): string {
  return now.toLocaleDateString('en-US', { month: 'long' });
}

export function formatClock(now = new Date()): string {
  return now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Arthur's report — the most valuable true sentence, then the next one
//
// Rules are checked in order and the first match wins. The sentence does
// not rotate: the same facts always produce the same lines.
//
// Arthur is the company, so the copy never uses a personal pronoun.
// A missing figure stays missing: it is not rendered as zero, and a
// reservation or order count is not described as Arthur's work unless
// the payload marks it as booked or placed through Arthur.
// ---------------------------------------------------------------------------

export interface HeroSegment {
  text: string;
  /** The figure or topic worth underlining. */
  emphasis?: boolean;
}

export interface HeroReport {
  /** Rule id, for tests and to avoid repeating the same figure underneath. */
  rule: string;
  segments: HeroSegment[];
  /** The next fact the headline did not already say. */
  secondary: string | null;
  /** Sample size for a topic line. Shown as a tooltip, not in the sentence. */
  secondaryTitle?: string | null;
  /** Confirmed paused: show that status instead of a setup link. */
  suppressConnect: boolean;
}

export interface MonthlyIntent {
  taggedCalls: number;
  /** Calls that could have been tagged, including the untagged ones. */
  eligibleCalls: number;
  topLabel: string;
  topCount: number;
}

export interface HeroInput {
  stats: DashboardStats;
  gating: ProductGating;
  /** `busy_mode_status`. Null means unknown, which is not offline. */
  busyStatus: string | null;
  /**
   * True only when an active assistant has a phone number. Null means
   * the check did not come back, so the headline does not say "ready".
   */
  routingActive: boolean | null;
  /** Store-local `YYYY-MM-DD`. The clock below is the fallback. */
  todayLocal: string | null;
  storeId: number | null;
  /**
   * A lifetime milestone crossed in the past seven days. Null means the
   * crossing is not known from outside. When omitted, the report derives
   * it from the lifetime total and the past-seven-day count. A current
   * total of 147 is not "100 calls".
   */
  milestoneCalls?: number | null;
  /** This month's intents. All-time breakdowns must not be passed here. */
  monthlyIntent?: MonthlyIntent | null;
  now?: Date;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const report = (
  rule: string,
  segments: HeroSegment[],
  secondary: string | null = null,
  suppressConnect = false,
  secondaryTitle: string | null = null,
): HeroReport => ({
  rule,
  segments,
  secondary,
  secondaryTitle,
  suppressConnect,
});

const segment = (value: string, emphasis = false): HeroSegment => ({
  text: value,
  emphasis,
});

/** Store-local calendar day. The browser clock is not a substitute. */
function localCalendar(
  todayLocal: string | null,
): { day: number; month: string } | null {
  const match = todayLocal?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const monthIndex = Number(match[2]);
  const day = Number(match[3]);
  if (monthIndex < 1 || monthIndex > 12 || day < 1 || day > 31) return null;
  return { day, month: MONTHS[monthIndex - 1] };
}

function statusToken(raw: string | null): string {
  return raw?.trim().toUpperCase() ?? '';
}

/** Hours and minutes, never rounded up. `12.9` minutes is `12 min`. */
export function formatCallDuration(minutes: number): string | null {
  if (!Number.isFinite(minutes)) return null;
  const total = Math.floor(minutes);
  if (total < 1) return null;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins.toLocaleString('en-US')} min`;
  const hourLabel = `${hours.toLocaleString('en-US')} hr`;
  if (mins === 0) return hourLabel;
  return `${hourLabel} ${mins.toLocaleString('en-US')} min`;
}

/**
 * A guest-facing phrase for "calling about". Internal keys (`ORDER`) are
 * not phrases, so they stay out of the sentence.
 */
export function topicPhrase(label: string): string | null {
  const trimmed = label.trim();
  if (!trimmed || /^[A-Z0-9_]+$/.test(trimmed)) return null;
  return trimmed.toLowerCase();
}

/** This month's top reason. An all-time breakdown is not this month. */
export function monthlyIntentFrom(
  data: CallIntelligence | null | undefined,
): MonthlyIntent | null {
  if (!data || data.scope !== 'month') return null;
  const top = [...(data.primary_breakdown ?? [])]
    .filter((row) => row.count > 0 && row.label.trim())
    .sort((a, z) => z.count - a.count)[0];
  if (!top) return null;
  return {
    taggedCalls: data.tagged_calls,
    eligibleCalls: data.tagged_calls + data.untagged_calls,
    topLabel: top.label,
    topCount: top.count,
  };
}

function isMilestone(n: number): boolean {
  if ([100, 250, 500, 1000, 2500, 5000, 10000].includes(n)) return true;
  return n > 10000 && n % 10000 === 0;
}

function milestoneMarks(upTo: number): number[] {
  const marks = [100, 250, 500, 1000, 2500, 5000, 10000];
  for (let n = 20000; n <= upTo; n += 10000) marks.push(n);
  return marks;
}

/** The highest milestone crossed between `before` and `all`, inclusive of `all`. */
function crossedInWindow(all: number, last7: number): number | null {
  if (last7 < 0 || all < last7) return null;
  const before = all - last7;
  let reached: number | null = null;
  for (const mark of milestoneMarks(all)) {
    if (before < mark && all >= mark) reached = mark;
  }
  return reached;
}

function resolvedMilestone(
  explicit: number | null | undefined,
  all: number | null,
  last7: number | null,
): number | null {
  if (explicit != null) return isMilestone(explicit) ? explicit : null;
  if (all == null || last7 == null) return null;
  return crossedInWindow(all, last7);
}

function qualifiedTopic(
  intent: MonthlyIntent | null | undefined,
): { phrase: string; title: string } | null {
  if (!intent) return null;
  const phrase = topicPhrase(intent.topLabel);
  if (!phrase) return null;
  if (intent.taggedCalls < 20 || intent.eligibleCalls <= 0) return null;
  if (intent.taggedCalls / intent.eligibleCalls < 0.75) return null;
  if (intent.topCount / intent.taggedCalls < 0.6) return null;
  return {
    phrase,
    title: `Based on ${plural(intent.taggedCalls, 'call', 'calls')} with a clear reason`,
  };
}

interface AlreadySaid {
  orders?: boolean;
  reservations?: boolean;
  monthCalls?: boolean;
  lifetime?: boolean;
}

/** Orders and reservations the headline has not already used. */
function bookingSecondary(
  input: HeroInput,
  said: AlreadySaid,
): { text: string; title: null } | null {
  const { stats, gating } = input;
  const orders =
    !said.orders && gating.orders === true ? stats.arthurOrdersMonth : null;
  const reservations =
    !said.reservations && gating.reservations === true
      ? stats.arthurReservationsMonth
      : null;
  if (
    orders != null &&
    orders >= 1 &&
    reservations != null &&
    reservations >= 1
  ) {
    return {
      text: `${plural(reservations, 'reservation', 'reservations')} booked and ${plural(orders, 'order', 'orders')} placed by Arthur.`,
      title: null,
    };
  }
  if (orders != null && orders >= 1) {
    return {
      text: `${plural(orders, 'order', 'orders')} placed by Arthur.`,
      title: null,
    };
  }
  if (reservations != null && reservations >= 1) {
    return {
      text: `${plural(reservations, 'reservation', 'reservations')} booked by Arthur.`,
      title: null,
    };
  }
  return null;
}

/** The next fact, skipping anything the headline already said or that is missing. */
function secondaryChain(
  input: HeroInput,
  said: AlreadySaid,
): { text: string; title: string | null } | null {
  const bookings = bookingSecondary(input, said);
  if (bookings) return bookings;

  const { stats } = input;
  if (!said.monthCalls) {
    const monthLine = monthCallsLine(input);
    if (monthLine) return { text: monthLine, title: null };
  }

  const topic = qualifiedTopic(input.monthlyIntent);
  if (topic) {
    return {
      text: `Most guests are calling about ${topic.phrase}.`,
      title: topic.title,
    };
  }

  const duration =
    stats.callMinutesMonth == null
      ? null
      : formatCallDuration(stats.callMinutesMonth);
  if (duration) {
    return { text: `${duration} on calls this month.`, title: null };
  }

  const allCalls = stats.callsAll;
  const monthCalls = stats.callsMonth;
  if (
    !said.lifetime &&
    allCalls != null &&
    monthCalls != null &&
    allCalls > monthCalls
  ) {
    return {
      text: `${plural(allCalls, 'call', 'calls')} answered all time.`,
      title: null,
    };
  }
  return null;
}

/** Calls this month, phrased so a zero never reads as "0 calls". */
function monthCallsLine(input: HeroInput): string | null {
  const monthCalls = input.stats.callsMonth;
  if (monthCalls == null) return null;
  const local = localCalendar(input.todayLocal);
  if (monthCalls === 0) {
    if (local && local.day <= 5)
      return `${local.month} is just getting started.`;
    return local
      ? `No calls in ${local.month} yet.`
      : 'No calls yet this month.';
  }
  if (monthCalls === 1) {
    return local
      ? `The first call of ${local.month} is in.`
      : 'The first call of this month is in.';
  }
  return `${plural(monthCalls, 'call', 'calls')} answered this month.`;
}

function ranked(
  rule: string,
  segments: HeroSegment[],
  input: HeroInput,
  said: AlreadySaid = {},
): HeroReport {
  const next = secondaryChain(input, said);
  return report(rule, segments, next?.text ?? null, false, next?.title ?? null);
}

/**
 * The navy card's sentence. The first matching rule wins, so a refresh
 * stays on the same lines until the facts change.
 */
export function heroReport(input: HeroInput): HeroReport {
  const { stats, gating } = input;
  const local = localCalendar(input.todayLocal);
  const monthCalls = stats.callsMonth;
  const allCalls = stats.callsAll;
  const paused = statusToken(input.busyStatus) === 'PAUSED';

  if (paused) {
    return report(
      'P1',
      [segment('Arthur is paused and isn’t answering calls.')],
      null,
      true,
    );
  }

  if (monthCalls == null) {
    if (allCalls != null) {
      return report(
        'M1',
        [
          segment('Arthur has answered '),
          segment(plural(allCalls, 'call', 'calls'), true),
          segment(' all time.'),
        ],
        'This month’s numbers aren’t available right now.',
      );
    }
    return report('M2', [segment('Call totals aren’t available right now.')]);
  }

  const milestone = resolvedMilestone(
    input.milestoneCalls,
    allCalls,
    stats.callsLast7Days,
  );
  if (milestone != null) {
    return ranked(
      'milestone',
      [
        segment('Arthur just reached '),
        segment(milestone.toLocaleString('en-US'), true),
        segment(' calls answered.'),
      ],
      input,
      { lifetime: allCalls === milestone },
    );
  }

  const orders =
    gating.orders === true && stats.arthurOrdersMonth != null
      ? stats.arthurOrdersMonth
      : null;
  const reservations =
    gating.reservations === true && stats.arthurReservationsMonth != null
      ? stats.arthurReservationsMonth
      : null;
  if (
    orders != null &&
    orders >= 3 &&
    reservations != null &&
    reservations >= 5
  ) {
    return ranked(
      'O1',
      [
        segment('Arthur has booked '),
        segment(plural(reservations, 'reservation', 'reservations'), true),
        segment(' and placed '),
        segment(plural(orders, 'order', 'orders'), true),
        segment(' this month.'),
      ],
      input,
      { orders: true, reservations: true },
    );
  }

  if (orders != null && orders >= 3) {
    return ranked(
      'O2',
      [
        segment('Arthur has placed '),
        segment(plural(orders, 'order', 'orders'), true),
        segment(' this month.'),
      ],
      input,
      { orders: true },
    );
  }

  if (reservations != null && reservations >= 5) {
    return ranked(
      'O3',
      [
        segment('Arthur has booked '),
        segment(plural(reservations, 'reservation', 'reservations'), true),
        segment(' this month.'),
      ],
      input,
      { reservations: true },
    );
  }

  const last7 = stats.callsLast7Days;
  if (
    local &&
    local.day <= 7 &&
    last7 != null &&
    last7 >= 5 &&
    last7 > monthCalls
  ) {
    const bookings = bookingSecondary(input, {});
    const secondary =
      bookings?.text ??
      (monthCalls <= 1
        ? monthCallsLine(input)
        : `${plural(monthCalls, 'call', 'calls')} so far in ${local.month}.`);
    return report(
      'E1',
      [
        segment('Arthur has answered '),
        segment(plural(last7, 'call', 'calls'), true),
        segment(' in the past seven days.'),
      ],
      secondary,
    );
  }

  if (
    monthCalls === 0 &&
    local &&
    local.day <= 5 &&
    allCalls != null &&
    allCalls > 0
  ) {
    return report('Q1', [
      segment(`${local.month} is just getting started. Arthur has answered `),
      segment(plural(allCalls, 'call', 'calls'), true),
      segment(' all time.'),
    ]);
  }

  if (monthCalls === 0) {
    return report(
      'Q2',
      [
        segment(
          local
            ? `No calls in ${local.month} yet.`
            : 'No calls yet this month.',
        ),
      ],
      allCalls != null && allCalls > 0
        ? `${plural(allCalls, 'call', 'calls')} answered all time.`
        : null,
    );
  }

  if (monthCalls === 1) {
    return ranked(
      'Q3',
      [
        segment(
          local
            ? `The first call of ${local.month} is in.`
            : 'The first call of this month is in.',
        ),
      ],
      input,
      { monthCalls: true },
    );
  }

  return ranked(
    'C1',
    [
      segment('Arthur has answered '),
      segment(plural(monthCalls, 'call', 'calls'), true),
      segment(' this month.'),
    ],
    input,
    { monthCalls: true },
  );
}

// ---------------------------------------------------------------------------
// Recent activity — a short merge of the calls, orders and reservations lists
// ---------------------------------------------------------------------------

/** How many rows each existing list is asked for. A page, not a history. */
export const ACTIVITY_PAGE = 6;

export type ActivityKind = 'call' | 'order' | 'reservation';

export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  at: string | null;
  href: string;
  hue: Hue;
  icon: 'phone' | 'calendar' | 'receipt';
  who: string | null;
  what: string;
  meta: string;
  value?: { amount: string; label: string };
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const text = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
};

const rowsOf = (payload: Payload | unknown[]): Record<string, unknown>[] => {
  if (Array.isArray(payload)) return payload.filter(isRecord);
  const results = payload?.results;
  return Array.isArray(results) ? results.filter(isRecord) : [];
};

const stamp = (...values: unknown[]): string | null => {
  for (const v of values) {
    if (typeof v === 'string' && !Number.isNaN(new Date(v).getTime())) return v;
  }
  return null;
};

const timeValue = (at: string | null) => {
  if (!at) return 0;
  const t = new Date(at).getTime();
  return Number.isNaN(t) ? 0 : t;
};

function callDuration(row: Record<string, unknown>): string | null {
  let ms = num(row.duration_ms);
  if (ms == null) {
    const start = stamp(row.start_time);
    const end = stamp(row.end_time);
    if (start && end) {
      const diff = new Date(end).getTime() - new Date(start).getTime();
      if (diff >= 0) ms = diff;
    }
  }
  if (ms == null || ms < 0) return null;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes}m ${seconds}s`;
}

function callItem(row: Record<string, unknown>): ActivityItem | null {
  const id = num(row.id);
  if (id == null) return null;
  const who = text(row.customer_name) ?? text(row.vip_name);
  const phone = text(formatPhone(text(row.from_number)));
  return {
    id: `call:${id}`,
    kind: 'call',
    at: stamp(row.start_time, row.created_at),
    href: '/calls',
    hue: 'sky',
    icon: 'phone',
    who,
    what: 'Call answered',
    meta: [callDuration(row), !who ? phone : null].filter(Boolean).join(' · '),
  };
}

function orderItem(row: Record<string, unknown>): ActivityItem | null {
  const id = num(row.order_id, row.id);
  if (id == null) return null;
  const customer = isRecord(row.customer) ? text(row.customer.name) : null;
  const fulfillment = listFulfillmentType(row);
  const cents = num(row.total_price);
  return {
    id: `order:${id}`,
    kind: 'order',
    at: stamp(row.created_at),
    href: '/orders',
    hue: 'teal',
    icon: 'receipt',
    who: text(row.customer_name) ?? customer,
    what:
      fulfillment === 'unknown'
        ? 'Order'
        : `${FULFILLMENT_LABEL[fulfillment]} order`,
    meta: text(row.status) ?? '',
    value:
      cents == null
        ? undefined
        : {
            amount: formatCurrencyFromCents(
              cents,
              text(row.currency) ?? undefined,
            ),
            label: 'total',
          },
  };
}

function reservationItem(row: Record<string, unknown>): ActivityItem | null {
  const id = text(row.reservation_id) ?? text(row.confirmation_code);
  if (!id) return null;
  const who =
    [text(row.guest_first_name), text(row.guest_last_name)]
      .filter(Boolean)
      .join(' ') || text(row.vip_name);
  const status = text(row.status);
  const party = num(row.party_size);
  const dining = stamp(row.start_time);
  const deposit = isRecord(row.deposit) ? row.deposit : null;
  const depositCents = deposit ? num(deposit.amount_cents) : null;
  return {
    id: `reservation:${id}`,
    kind: 'reservation',
    at: stamp(row.updated_at, row.created_at, row.start_time),
    href: '/reservations/opentable',
    hue: 'violet',
    icon: 'calendar',
    who,
    what: status ? `${sentenceCaseStatus(status)} reservation` : 'Reservation',
    meta: [
      party != null && party >= 0
        ? `${party.toLocaleString('en-US')} ${party === 1 ? 'guest' : 'guests'}`
        : null,
      dining &&
        new Date(dining).toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
    ]
      .filter(Boolean)
      .join(' · '),
    value:
      depositCents != null && depositCents > 0
        ? {
            amount: formatCurrencyFromCents(
              depositCents,
              deposit ? (text(deposit.currency) ?? undefined) : undefined,
            ),
            label: 'deposit',
          }
        : undefined,
  };
}

/**
 * Newest first, capped. Orders and reservations the store is known not to
 * have are left out; an unreadable status (`null`) keeps the rows, same as
 * the figures.
 */
export function buildActivity(
  calls: Payload,
  orders: Payload,
  reservations: Payload | unknown[],
  gating: ProductGating,
): ActivityItem[] {
  const items = [
    ...rowsOf(calls).map(callItem),
    ...(gating.orders === false ? [] : rowsOf(orders).map(orderItem)),
    ...(gating.reservations === false
      ? []
      : rowsOf(reservations).map(reservationItem)),
  ].filter((item): item is ActivityItem => item != null);
  items.sort((a, z) => timeValue(z.at) - timeValue(a.at));
  return items.slice(0, ACTIVITY_PAGE);
}

/** "Just now" or a clock time today; older rows use the shared relative time. */
export function activityWhen(at: string | null, now = new Date()): string {
  if (!at) return '';
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  if (d.toDateString() === now.toDateString()) {
    const rel = relativeTime(at, now);
    if (rel === 'just now') return 'Just now';
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return relativeTime(at, now);
}

const FRESH_MS = 10 * 60 * 1000;

export function isFresh(at: string | null, now = new Date()): boolean {
  if (!at) return false;
  const age = now.getTime() - new Date(at).getTime();
  return age >= 0 && age < FRESH_MS;
}

export function latestCallAt(items: ActivityItem[]): string | null {
  let best: string | null = null;
  let bestTime = 0;
  for (const item of items) {
    if (item.kind !== 'call' || !item.at) continue;
    const t = timeValue(item.at);
    if (t >= bestTime) {
      best = item.at;
      bestTime = t;
    }
  }
  return best;
}
